import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { injectIntl } from 'react-intl';
import CSSModules from 'react-css-modules';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import lowerCase from 'lodash/lowerCase';
import upperFirst from 'lodash/upperFirst';

import {
  ADD_PROVIDER_OPTION,
  ADD_PROVIDER_REASON_CODE,
  NEW_CHAT,
} from '../../../../constants/strings';
import ClickableElement from '../../../ClickableElement';
import Icon from '../../../Icon';
import { COLORS } from '../../../../constants/styles';
import Tooltip from '../../Tooltip';
import SingleSelect from '../../SingleSelect';
import { getListLocalization } from '../../../../utils/converters/getListLocalization';
import SelectCustom from '../../../SelectCustom';
import ErrorBlock from '../../ErrorBlock';
import { useToggleFlag } from '../../../../hocs/useToogleFlag';
import { selectRelationshipPriority } from '../../../../redux/board/selectors/relationshipPriority';
import styles from './styles.styl';

const INPUT_WIDTH = 290;

const AssignmentFormImpl = ({ intl: { formatMessage }, onFormChange }) => {
  const [tipOpened, openTip, closeTip] = useToggleFlag();
  const [reason, setReason] = useState(null);
  const [relation, setRelation] = useState(null);
  const [level, setLevel] = useState('permanent');

  const relationship = useSelector(selectRelationshipPriority);

  const adaptName = name => upperFirst(lowerCase(name));

  const relationshipOptions = useMemo(
    () =>
      relationship.map(item => ({
        label: adaptName(item.name),
        value: item.id,
      })),
    [relationship],
  );

  const reasonOptions = useMemo(() => {
    switch (level) {
      case 'temporary': {
        return getListLocalization(
          ADD_PROVIDER_REASON_CODE.temporary,
          formatMessage,
        );
      }
      default: {
        return getListLocalization(
          ADD_PROVIDER_REASON_CODE.permanent,
          formatMessage,
        );
      }
    }
  }, [level]);

  const handleOptionChange = useCallback(value => {
    setLevel(value);
    setReason(null);
  }, []);

  useEffect(() => {
    onFormChange({
      isValid: !!reason && !!relation,
      data: {
        level,
        reason: get(reason, 'value', null),
        relation: get(relation, 'value', null),
      },
    });
  }, [reason, level, relation]);

  const error = false;

  return (
    <div styleName="form">
      <div styleName="form-content">
        <div className="mr3">
          <div className="flex">
            <span styleName="form-label">
              {formatMessage(NEW_CHAT.accessLevel)}
            </span>
            <div
              className="relative"
              onMouseEnter={openTip}
              onMouseLeave={closeTip}
            >
              <ClickableElement>
                <Icon
                  name="info-default"
                  size={12}
                  style={{ fill: COLORS.darkSlateBlue }}
                  className="db"
                />
              </ClickableElement>
              <Tooltip
                content={
                  <p className="f-12 lh-20">
                    {formatMessage(NEW_CHAT.accessLevelTooltip)}
                  </p>
                }
                top={15}
                left={0}
                width={225}
                open={tipOpened}
              />
            </div>
          </div>
          <SingleSelect
            name="providerOption"
            list={getListLocalization(ADD_PROVIDER_OPTION, formatMessage)}
            selectClass="flex"
            radioInputClass="mr2"
            selectedValue={level}
            onChange={handleOptionChange}
          />
        </div>
        <div styleName="form-selects">
          <SelectCustom
            style={{ width: INPUT_WIDTH }}
            styleName="form-select"
            placeholder={formatMessage(NEW_CHAT.selectReasonForAssignment)}
            label={formatMessage(NEW_CHAT.reasonForAssignment)}
            options={reasonOptions}
            value={reason}
            onChange={setReason}
            needUpdateDefaultValue
          />
          <SelectCustom
            style={{ width: INPUT_WIDTH }}
            styleName="form-select"
            placeholder={formatMessage({
              id: 'relationshipPriority.subtitle',
            })}
            label={formatMessage({
              id: 'relationshipPriority.title',
            })}
            options={relationshipOptions}
            value={relation}
            onChange={setRelation}
            needUpdateDefaultValue
          />
        </div>
      </div>
      <If condition={error}>
        <ErrorBlock
          errors={formatMessage(NEW_CHAT.selectReasonForAssignment)}
          className="w-80 mt3"
        />
      </If>
    </div>
  );
};

AssignmentFormImpl.propTypes = {
  intl: PropTypes.object.isRequired,
  onFormChange: PropTypes.func.isRequired,
};

export const AssignmentForm = compose(
  injectIntl,
  CSSModules(styles, { allowMultiple: true }),
)(AssignmentFormImpl);



// WEBPACK FOOTER //
// ./src/components/common/SearchDialogWithForm/AssigmentForm/index.jsx